import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    SelectChangeEvent,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReviewEntityMetricsTrending, {
    BreadcrumbItem,
} from '../../components/ReviewEntityMetricsTrending/ReviewEntityMetricsTrending';
import { getMyReviewEntities, ReviewEntity, HierarchyChildInfo } from '../../services/api';

interface MetricsTrendingContentProps {
    initialEntityId?: number | null;
    lockEntitySelection?: boolean;
    compact?: boolean;
}

const MetricsTrendingContent: React.FC<MetricsTrendingContentProps> = ({
    initialEntityId = null,
    lockEntitySelection = false,
    compact = false,
}) => {
    const [reviewEntities, setReviewEntities] = useState<ReviewEntity[]>([]);
    const [selectedEntityId, setSelectedEntityId] = useState<number | ''>(initialEntityId || '');
    const [navigationPath, setNavigationPath] = useState<BreadcrumbItem[]>([]);
    const [loadingEntities, setLoadingEntities] = useState(true);

    useEffect(() => {
        const fetchReviewEntities = async () => {
            try {
                setLoadingEntities(true);
                const response = await getMyReviewEntities();
                const entities = response.data || [];
                setReviewEntities(entities);

                if (initialEntityId) {
                    setSelectedEntityId(initialEntityId);
                    const initialEntity = entities.find((entity) => entity.id === initialEntityId);
                    if (initialEntity) {
                        setNavigationPath([{ id: initialEntity.id, name: initialEntity.name }]);
                    }
                }
            } catch (err: any) {
                console.error('Error fetching review entities:', err);
            } finally {
                setLoadingEntities(false);
            }
        };
        fetchReviewEntities();
    }, [initialEntityId]);

    const handleEntityChange = (event: SelectChangeEvent<number | ''>) => {
        if (lockEntitySelection) {
            return;
        }

        const newEntityId = event.target.value as number | '';
        setSelectedEntityId(newEntityId);
        if (newEntityId) {
            const entity = reviewEntities.find((e) => e.id === newEntityId);
            if (entity) {
                setNavigationPath([{ id: entity.id, name: entity.name }]);
            }
        } else {
            setNavigationPath([]);
        }
    };

    const handleDrillDown = (child: HierarchyChildInfo) => {
        setSelectedEntityId(child.id);
        setNavigationPath((prev) => [...prev, { id: child.id, name: child.name }]);
    };

    const handleBreadcrumbClick = (index: number) => {
        const targetItem = navigationPath[index];
        setSelectedEntityId(targetItem.id);
        setNavigationPath((prev) => prev.slice(0, index + 1));
    };

    return (
        <Paper
            elevation={compact ? 0 : 3}
            sx={{
                p: compact ? 2 : 4,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
            }}
        >
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Metrics Trending Report
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    Track how metric values have changed over time for your review entities
                </Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    bgcolor: 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FilterListIcon sx={{ color: 'text.secondary' }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        Filters
                    </Typography>
                </Box>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <FormControl fullWidth disabled={loadingEntities || lockEntitySelection}>
                            <InputLabel id="entity-select-label">Review Entity *</InputLabel>
                            <Select
                                labelId="entity-select-label"
                                value={selectedEntityId}
                                onChange={handleEntityChange}
                                label="Review Entity *"
                            >
                                {!lockEntitySelection && (
                                    <MenuItem value="">
                                        <em>Select a review entity</em>
                                    </MenuItem>
                                )}
                                {reviewEntities.map((entity) => (
                                    <MenuItem key={entity.id} value={entity.id}>
                                        {entity.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {loadingEntities && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Loading your review entities...
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            <ReviewEntityMetricsTrending
                reviewEntityId={selectedEntityId || null}
                includeAggregate={true}
                showEntityNavigation={true}
                navigationPath={navigationPath}
                onDrillDown={handleDrillDown}
                onBreadcrumbClick={handleBreadcrumbClick}
                showSelectEntityPrompt={!lockEntitySelection}
            />
        </Paper>
    );
};

export default MetricsTrendingContent;
